// src/components/roads/roadAiPredictions.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Road AI prediction layer (pure JS, no React)
//
// One place that turns a road record + its ML output into what the UI shows:
//   • remaining useful life (RUL) — from the Random Survival Forest when it exists,
//     otherwise a clearly-labelled rule-based estimate
//   • risk index (0–100) with the drivers that explain it
//   • recommended actions, each with an intent the tables can execute
//   • data readiness (from AiRoadHelper.analyzeRoad)
//   • a network-level summary for the AI panels
//
// ai_rul fields are read defensively because the ML service has returned several
// shapes over time (confidence 0–1 or 0–100, predicted_at vs updated_at, …).
// --- UPDATE (roads v1.4): road ponding forecast (road.ai_ponding) → risk driver + action ("Clear the drains /
// pre-stage a pump"), shown in the risk index, prediction card and tables.
// ─────────────────────────────────────────────────────────────────────────────
import { analyzeRoad, normalizeRoad } from './AiRoadHelper';
import { RUL_BANDS, rulBand } from './page/shared';

export { RUL_BANDS, rulBand };

const YEAR_MS = 365.25 * 86400000;
const SEA_LEVEL_RISE_M = 0.23;

/** Typical design life by surface (years). Used only for the rule-based estimate. */
export const SURFACE_LIFESPAN = Object.freeze({
    concrete: 30, asphalt: 18, bituminous: 15, chip_seal: 8, 'chip seal': 8, surface_dressing: 8,
    pavers: 25, gravel: 5, dirt: 3, unpaved: 3,
});
const DEFAULT_LIFESPAN = 15;

const toNum = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const firstOf = (obj, keys) => {
    if (!obj) return undefined;
    for (const k of keys) if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
    return undefined;
};
/** 0–1 from either 0–1 or 0–100 input. */
const toUnit = (v) => {
    const n = toNum(v);
    if (n == null) return null;
    return Math.max(0, Math.min(1, n > 1 ? n / 100 : n));
};
const roundHalf = (n) => Math.round(n * 2) / 2;

export const RISK_LEVELS = [
    { key: 'critical', label: 'Critical', min: 70, color: 'red' },
    { key: 'high', label: 'High', min: 50, color: 'orange' },
    { key: 'medium', label: 'Medium', min: 30, color: 'yellow' },
    { key: 'low', label: 'Low', min: 0, color: 'green' },
];
export const riskLevel = (score) => RISK_LEVELS.find(l => score >= l.min) || RISK_LEVELS[RISK_LEVELS.length - 1];

export const confidenceLabel = (c) => (c == null ? null : c >= 0.8 ? { label: 'High', color: 'green' } : c >= 0.6 ? { label: 'Moderate', color: 'yellow' } : { label: 'Low', color: 'orange' });

/** Action intents understood by RoadTableRow / ViewRoadModal callers. */
export const ACTION_INTENTS = Object.freeze({
    CREATE_WO: 'CREATE_WO', SCHEDULE_INSPECTION: 'SCHEDULE_INSPECTION', ASSESS: 'ASSESS',
    EDIT: 'EDIT', MAP: 'MAP', RUN_AI: 'RUN_AI', VIEW: 'VIEW',
});

/** Insights from roadAIEngineService.getNetworkAIInsights() that belong to one road. */
export const insightRoadId = (ins) => firstOf(ins, ['road_id', 'roadId', 'asset_id', 'assetId', 'target_id', 'entity_id']);
export const groupInsightsByRoad = (insights) => {
    const map = new Map();
    (Array.isArray(insights) ? insights : []).forEach(ins => {
        const id = insightRoadId(ins);
        if (id === undefined) return;
        const key = String(id);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(ins);
    });
    return map;
};

/** Read the ML output in whatever shape it arrives. */
export const readModelOutput = (aiRul) => {
    if (!aiRul || typeof aiRul !== 'object') return null;
    const rul = toNum(firstOf(aiRul, ['predicted_rul_years', 'rul_years', 'rul']));
    const lower = toNum(firstOf(aiRul, ['rul_lower', 'ci_lower', 'lower_bound', 'p10']));
    const upper = toNum(firstOf(aiRul, ['rul_upper', 'ci_upper', 'upper_bound', 'p90']));
    const confidence = toUnit(firstOf(aiRul, ['confidence', 'confidence_score', 'model_confidence']));
    const failureProb = toUnit(firstOf(aiRul, ['failure_probability_12m', 'failure_probability', 'risk_probability', 'probability_of_failure']));
    const predictedAt = firstOf(aiRul, ['predicted_at', 'generated_at', 'updated_at', 'created_at', 'timestamp']) || null;
    const modelVersion = firstOf(aiRul, ['model_version', 'model', 'model_name']) || null;
    const action = firstOf(aiRul, ['recommended_action', 'prescriptive_action', 'action']) || null;
    let features = firstOf(aiRul, ['feature_importance', 'top_features', 'drivers']);
    if (features && !Array.isArray(features) && typeof features === 'object') {
        features = Object.entries(features).map(([feature, importance]) => ({ feature, importance }));
    }
    features = Array.isArray(features)
        ? features
            .map(f => (typeof f === 'string' ? { feature: f, importance: null } : { feature: f.feature || f.name, importance: toNum(f.importance ?? f.weight ?? f.value) }))
            .filter(f => f.feature)
            .sort((a, b) => (b.importance ?? 0) - (a.importance ?? 0))
            .slice(0, 5)
        : [];
    if (rul == null && failureProb == null && !action) return null;
    return { rul, lower, upper, confidence, failureProb, predictedAt, modelVersion, action, features, live: !!aiRul.live };
};

/** Rule-based RUL when the ML model has not scored the road yet. */
export const estimateRul = (n, now = Date.now()) => {
    const surfaceKey = String(n.surface_type || '').toLowerCase().trim();
    const lifespan = n.expected_lifespan_years > 0 ? n.expected_lifespan_years : (SURFACE_LIFESPAN[surfaceKey] || DEFAULT_LIFESPAN);
    let byAge = null;
    if (n.install_date) {
        const t = new Date(n.install_date).getTime();
        if (Number.isFinite(t) && t <= now) byAge = Math.max(0, lifespan - (now - t) / YEAR_MS);
    }
    let byCondition = null;
    if (n.condition_score != null) {
        // Condition 100 → full lifespan, 25 or below → end of life
        byCondition = Math.max(0, lifespan * Math.max(0, n.condition_score - 25) / 75);
    }
    const candidates = [byAge, byCondition].filter(v => v != null);
    if (!candidates.length) return null;
    return {
        rul: roundHalf(Math.min(...candidates)),
        basis: [byAge != null && 'age', byCondition != null && 'condition'].filter(Boolean),
        lifespan,
    };
};

const AGE_LABEL = (n, now) => {
    if (!n.install_date) return null;
    const t = new Date(n.install_date).getTime();
    if (!Number.isFinite(t)) return null;
    const y = (now - t) / YEAR_MS;
    return y >= 0 ? `${y.toFixed(1)} yrs` : null;
};

/**
 * Build the full prediction view-model for one road.
 * @param {object} road   API road (may include ai_rul and ai_insights)
 * @param {{ insights?: object[], now?: number }} opts
 */
export function buildRoadPrediction(road, opts = {}) {
    const now = opts.now ?? Date.now();
    const analysis = analyzeRoad(road || {}, 'view');
    const n = analysis.normalized || normalizeRoad(road || {});
    const model = readModelOutput(road?.ai_rul);
    const estimate = model?.rul != null ? null : estimateRul(n, now);

    const source = model?.rul != null ? 'model' : estimate ? 'estimate' : 'none';
    const rulYears = source === 'model' ? model.rul : source === 'estimate' ? estimate.rul : null;
    const band = rulBand(rulYears);
    const failureDate = rulYears != null ? new Date(now + rulYears * YEAR_MS) : null;

    // ── Risk index ──────────────────────────────────────────────────────────
    const drivers = [];
    let risk = 0;
    const addDriver = (points, label, detail) => { risk += points; drivers.push({ points, label, detail }); };

    if (rulYears == null) addDriver(25, 'No life prediction', 'Unknown remaining life is treated as moderate risk until the model scores this road.');
    else if (band?.key === 'critical') addDriver(55, `${rulYears} yrs remaining life`, 'Within 2 years of needing rehabilitation.');
    else if (band?.key === 'poor') addDriver(38, `${rulYears} yrs remaining life`, 'Within the 5-year planning window.');
    else if (band?.key === 'fair') addDriver(20, `${rulYears} yrs remaining life`, 'Deteriorating — monitor.');
    else addDriver(8, `${rulYears} yrs remaining life`, 'Healthy service life.');

    if (n.condition_score != null) {
        if (n.condition_score < 40) addDriver(20, `Condition ${n.condition_score}/100`, 'Critical pavement condition from the last inspection.');
        else if (n.condition_score < 55) addDriver(14, `Condition ${n.condition_score}/100`, 'Below the 55 maintenance threshold.');
        else if (n.condition_score < 70) addDriver(6, `Condition ${n.condition_score}/100`, 'Fair condition.');
    }
    const overdueDays = n.next_inspection_due ? Math.floor((now - new Date(n.next_inspection_due).getTime()) / 86400000) : null;
    if (overdueDays != null && overdueDays > 0) addDriver(8, `Inspection ${overdueDays} days overdue`, 'Condition data is going stale.');
    if (n.open_wo > 0) addDriver(Math.min(9, 3 * n.open_wo), `${n.open_wo} open work order${n.open_wo > 1 ? 's' : ''}`, 'Known defects not yet repaired.');
    if (n.elevation != null && n.elevation - SEA_LEVEL_RISE_M < 1.8) addDriver(8, `Low-lying (${n.elevation} m)`, 'Exposed to storm surge and ponding.');

    // --- UPDATE: live hydraulic flood alert from useRoadWorksData (ai_flood_alert)
    const floodAlert = road?.ai_flood_alert || null;
    if (floodAlert) addDriver(12, `Flood risk ${floodAlert.probability ?? '—'}%${floodAlert.well_name || floodAlert.well_id ? ` (well ${floodAlert.well_name || floodAlert.well_id})` : ''}`, 'Water in the road sub-base speeds up cracking and pothole formation.');
    // --- END UPDATE
    // --- UPDATE (roads v1.4): live rainfall forecast says water will stand on this road
    const ponding = road?.ai_ponding && road.ai_ponding.band && road.ai_ponding.band !== 'none' ? road.ai_ponding : null;
    if (ponding) {
        const pts = ponding.band === 'severe' ? 14 : ponding.band === 'high' ? 10 : 5;
        const cm = ponding.max_depth_m != null ? Math.round(Number(ponding.max_depth_m) * 100) : null;
        addDriver(pts, `Rain water forecast${cm ? ` (${cm} cm)` : ''} on ${ponding.ponding_segments} segment(s)`, `Standing water from the live rainfall forecast${ponding.first_hours != null ? `, from about +${Math.round(ponding.first_hours)} h` : ''} — ponding softens the base and opens potholes.`);
    }
    // --- END UPDATE
    if (model?.failureProb != null) {
        const p = Math.round(model.failureProb * 100);
        if (p > risk) drivers.push({ points: p - risk, label: `Model failure probability ${p}%`, detail: 'The ML model\'s own 12-month failure probability raised the index.' });
        risk = Math.max(risk, p);
    }
    const riskScore = Math.max(0, Math.min(100, Math.round(risk)));
    const level = riskLevel(riskScore);
    drivers.sort((a, b) => b.points - a.points);

    // ── Recommended actions ────────────────────────────────────────────────
    const actions = [];
    const push = (a) => { if (!actions.some(x => x.intent === a.intent && x.label === a.label)) actions.push(a); };
    // --- UPDATE
    if (floodAlert) push({ intent: ACTION_INTENTS.CREATE_WO, label: 'Inspect sub-base drainage and pre-stage dewatering', priority: 'critical', explain: `Hydraulic AI flagged ${String(floodAlert.severity || 'high').toLowerCase()} flood risk near this road.`, fromModel: true });
    // --- END UPDATE
    // --- UPDATE (roads v1.4)
    if (ponding && (ponding.band === 'high' || ponding.band === 'severe')) push({ intent: ACTION_INTENTS.CREATE_WO, label: 'Clear the drains and pre-stage a pump before the rain', priority: ponding.band === 'severe' ? 'critical' : 'high', explain: `The road ponding forecast puts ${Math.round(Number(ponding.max_depth_m || 0) * 100)} cm of water on ${ponding.ponding_segments} segment(s).`, fromModel: true });
    // --- END UPDATE
    if (model?.action) push({ intent: ACTION_INTENTS.CREATE_WO, label: String(model.action), priority: band?.key === 'critical' ? 'critical' : 'high', explain: 'Prescribed by the ML model.', fromModel: true });
    if (band?.key === 'critical') push({ intent: ACTION_INTENTS.CREATE_WO, label: 'Schedule rehabilitation (mill & overlay or reconstruction)', priority: 'critical', explain: 'Less than 2 years of service left — budget major works now.' });
    else if (band?.key === 'poor') push({ intent: ACTION_INTENTS.CREATE_WO, label: 'Plan preventive maintenance (crack seal, patching, surface treatment)', priority: 'high', explain: 'Cheap preventive work now avoids full reconstruction later.' });
    else if (band?.key === 'fair') push({ intent: ACTION_INTENTS.SCHEDULE_INSPECTION, label: 'Inspect within 6 months', priority: 'medium', explain: 'Confirm the deterioration rate before it reaches the planning window.' });
    if (overdueDays != null && overdueDays > 0) push({ intent: ACTION_INTENTS.SCHEDULE_INSPECTION, label: 'Book the overdue inspection', priority: 'high', explain: `${overdueDays} days overdue.` });
    if (n.condition_score == null && !n.condition_rating) push({ intent: ACTION_INTENTS.ASSESS, label: 'Record a condition assessment', priority: 'medium', explain: 'Gives the model a real observation instead of age alone.' });
    if (n.condition_score != null && n.condition_score < 55 && n.status === 'operational') push({ intent: ACTION_INTENTS.EDIT, label: 'Set status to "Maintenance required"', priority: 'medium', explain: `Condition ${n.condition_score}/100 is below the maintenance threshold.` });
    if (!n.hasGeometry) push({ intent: ACTION_INTENTS.MAP, label: 'Draw the road on the GIS map', priority: 'medium', explain: 'Unlocks the risk heatmap and pothole clustering.' });
    if (source !== 'model' && analysis.missingRequired.length === 0) push({ intent: ACTION_INTENTS.RUN_AI, label: 'Run the ML remaining-life prediction', priority: 'low', explain: source === 'estimate' ? 'Replace the rough estimate with a model forecast.' : 'Enough data is present for a first prediction.' });
    if (!actions.length) push({ intent: ACTION_INTENTS.SCHEDULE_INSPECTION, label: 'Keep the routine inspection cycle', priority: 'low', explain: 'No action needed beyond normal monitoring.' });

    // ── What the model used ────────────────────────────────────────────────
    const inputs = [
        { key: 'age', label: 'Age', value: AGE_LABEL(n, now), field: 'install_date' },
        { key: 'surface', label: 'Surface', value: n.surface_type || null, field: 'surface_type' },
        { key: 'class', label: 'Traffic class', value: n.classification || null, field: 'classification' },
        { key: 'condition', label: 'Condition', value: n.condition_score != null ? `${n.condition_score}/100` : n.condition_rating || null, field: 'condition' },
        { key: 'elevation', label: 'Elevation', value: n.elevation != null ? `${n.elevation} m` : null, field: 'elevation' },
        { key: 'geometry', label: 'Mapped', value: n.hasGeometry ? 'Yes' : null, field: 'geometry' },
    ];

    const roadInsights = Array.isArray(road?.ai_insights) ? road.ai_insights
        : opts.insights ? (groupInsightsByRoad(opts.insights).get(String(road?.id)) || []) : [];

    const summary = source === 'model'
        ? `The AI predicts about ${rulYears} year${rulYears === 1 ? '' : 's'} of service left (${band.label.toLowerCase()}), with a ${level.label.toLowerCase()} risk index of ${riskScore}. ${actions[0].label}.`
        : source === 'estimate'
            ? `No ML prediction yet — a rough estimate from ${estimate.basis.join(' and ')} suggests ~${rulYears} years. ${actions[0].label}.`
            : `Not enough data to predict remaining life. ${analysis.summary}`;

    return {
        source, rulYears, band, failureDate,
        lower: model?.lower ?? null, upper: model?.upper ?? null,
        confidence: model?.confidence ?? null,
        confidenceInfo: confidenceLabel(model?.confidence ?? null),
        failureProb: model?.failureProb ?? null,
        predictedAt: model?.predictedAt ?? null,
        modelVersion: model?.modelVersion ?? null,
        features: model?.features ?? [],
        live: !!model?.live,
        estimate,
        riskScore, riskLevel: level, drivers,
        actions,
        inputs,
        insights: roadInsights,
        readiness: { score: analysis.score, grade: analysis.grade, missingRequired: analysis.missingRequired, models: analysis.models },
        overdueDays: overdueDays != null && overdueDays > 0 ? overdueDays : null,
        floodAlert, // --- UPDATE
        ponding, // --- UPDATE (roads v1.4)
        hasGeometry: n.hasGeometry,
        normalized: n,
        summary,
    };
}

// Memo per road object — rows, panels and CSV export share one computation.
const cache = new WeakMap();
export const getRoadPrediction = (road, opts = {}) => {
    if (!road || typeof road !== 'object') return buildRoadPrediction(road, opts);
    const hit = cache.get(road);
    if (hit && hit.insights === opts.insights) return hit.value;
    const value = buildRoadPrediction(road, opts);
    cache.set(road, { insights: opts.insights, value });
    return value;
};

/** Network roll-up for AIOperationsPanel / dashboards. */
export function summarizeNetwork(roads = []) {
    const bands = Object.fromEntries(RUL_BANDS.map(b => [b.key, 0]));
    const blocked = {};
    let modelCount = 0, estimateCount = 0, rulSum = 0, rulN = 0, readinessSum = 0, aiReady = 0, unmapped = 0;
    const ranked = [];
    (Array.isArray(roads) ? roads : []).forEach(r => {
        const p = getRoadPrediction(r);
        if (p.source === 'model') { modelCount += 1; rulSum += p.rulYears; rulN += 1; }
        if (p.source === 'estimate') estimateCount += 1;
        if (p.band) bands[p.band.key] += 1;
        readinessSum += p.readiness.score;
        if (p.readiness.score >= 85) aiReady += 1;
        if (!p.hasGeometry) unmapped += 1;
        p.readiness.models.forEach(m => { if (m.state === 'blocked') blocked[m.key] = (blocked[m.key] || 0) + 1; });
        ranked.push({ road: r, prediction: p });
    });
    const total = ranked.length;
    ranked.sort((a, b) => b.prediction.riskScore - a.prediction.riskScore);
    return {
        total, modelCount, estimateCount,
        coverage: total ? Math.round((modelCount / total) * 100) : 0,
        averageRUL: rulN ? Number((rulSum / rulN).toFixed(1)) : null,
        bands,
        readinessAvg: total ? Math.round(readinessSum / total) : 0,
        aiReady, unmapped, blocked,
        topRisk: ranked.slice(0, 5),
    };
}

/** Patch a road's ai_rul from a live websocket prediction. Returns a new road object. */
export const applyLivePrediction = (road, data = {}) => {
    const patch = { live: true, predicted_at: data.timestamp || new Date().toISOString() };
    const rul = toNum(firstOf(data, ['predicted_rul_years', 'rul_years', 'rul']));
    if (rul != null) patch.predicted_rul_years = rul;
    if (data.confidence != null) patch.confidence = data.confidence;
    if (data.action) patch.recommended_action = data.action;
    const fp = firstOf(data, ['failure_probability', 'failure_probability_12m']);
    if (fp != null) patch.failure_probability = fp;
    return { ...road, ai_rul: { ...(road.ai_rul || {}), ...patch } };
};